import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

function useCountUp(target, duration = 1600) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!target || target <= 0) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

function Counter({ value, label, hint, suffix }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const n = useCountUp(inView ? value : 0);
  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="font-serif-display text-6xl sm:text-7xl text-[#E5A93C] leading-none">
        {n.toLocaleString("it-IT")}
        {suffix && <span className="text-3xl ml-1">{suffix}</span>}
      </div>
      <div className="av-eyebrow mt-4">{label}</div>
      {hint && <p className="text-xs text-[#A39E93] mt-2 max-w-[220px] mx-auto">{hint}</p>}
    </div>
  );
}

export default function PublicStats() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/stats/public").then((r) => setS(r.data));
  }, []);
  if (!s) return null;
  return (
    <section
      className="border-y av-hairline bg-[#141210]/40"
      data-testid="public-stats"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <span className="av-eyebrow">In numeri</span>
          <h2 className="font-serif-display text-3xl sm:text-5xl mt-3 leading-tight">
            Ogni evento è una <span className="italic text-[#E5A93C]">storia</span>.
            <br className="hidden sm:block" />
            Queste sono le <span className="italic text-[#E5A93C]">nostre</span>.
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 mt-14">
          <Counter value={s.events_done} label="Eventi prodotti" hint="Sagre, gala, tour, matrimoni, casting" />
          <Counter value={s.cities_touched} label="Città toccate" hint="Dal Sud alle Alpi, festival e piazze" />
          <Counter value={s.artists_in_roster} label="Artisti in roster" hint="Selezionati e sotto contratto NMA" />
          <Counter value={s.shows_in_catalog} label="Format spettacolo" hint="Pronti a partire, chiavi in mano" />
        </div>
      </div>
    </section>
  );
}
